/**
 * CompaniesListPage — table view over vault_companies for the current
 * Holder. Mirror of fias360/AccountsListPage with sales-flow vocabulary.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';
import { ARCHE_PATH } from '../constants';
import {
  useFiasLeadsCompanies,
  useCreateFiasLeadsCompany,
  useFiasLeadsCrossArchePresence,
  useBulkArchiveFiasLeadsCompanies,
  useBulkRestoreFiasLeadsCompanies,
  useBulkDeleteFiasLeadsCompanies,
} from '../hooks/useFiasLeads';
import { useListSelection } from '../hooks/useListSelection';
import { useToast } from '@/components/ui/use-toast';
import { SkeletonRow } from '../components/Skeletons';
import { BulkArchiveBar } from '../components/BulkArchiveBar';
import { BulkDeleteDialog } from '../components/BulkDeleteDialog';
import { CrossArcheBadge } from '../components/CrossArcheBadge';
import { SortableTh, sortRows, useTableSort, type SortValue } from '../lib/table-sort';
import { summarizeBulkDelete } from '../lib/bulk-delete-failures';

type CompanySortKey = 'name' | 'industry' | 'domain' | 'updated';

interface Props {
  holderId: string;
}

export function CompaniesListPage({ holderId }: Props) {
  const [showArchived, setShowArchived] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { toast } = useToast();
  // 500 is the server-side cap on this list (see ContactsListPage).
  const companiesQuery = useFiasLeadsCompanies(holderId, {
    includeArchived: showArchived || undefined,
    limit: 500,
  });
  const createCompany = useCreateFiasLeadsCompany(holderId);
  const presenceQuery = useFiasLeadsCrossArchePresence(
    holderId,
    [],
    (companiesQuery.data ?? []).map((c) => c.companyId),
  );
  const companyPresence = presenceQuery.data?.companyPresence ?? {};
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [industry, setIndustry] = useState('');
  const [search, setSearch] = useState('');

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await createCompany.mutateAsync({
      name: name.trim(),
      domain: domain.trim() || undefined,
      industry: industry.trim() || undefined,
    });
    setName('');
    setDomain('');
    setIndustry('');
    setShowCreate(false);
  };

  const filtered = useMemo(() => {
    const all = (companiesQuery.data ?? []).filter((c) =>
      showArchived ? !!c.archivedAt : !c.archivedAt,
    );
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.domain ?? '').toLowerCase().includes(q) ||
        (c.industry ?? '').toLowerCase().includes(q),
    );
  }, [companiesQuery.data, search, showArchived]);

  const sort = useTableSort<CompanySortKey>();
  const sorted = useMemo(() => {
    if (!sort.sortKey) return filtered; // server order (updated_at DESC)
    const key = sort.sortKey;
    const accessor = (c: (typeof filtered)[number]): SortValue => {
      switch (key) {
        case 'name':
          return c.name;
        case 'industry':
          return c.industry;
        case 'domain':
          return c.domain;
        case 'updated':
          return new Date(c.updatedAt);
      }
    };
    return sortRows(filtered, accessor, sort.direction);
  }, [filtered, sort.sortKey, sort.direction]);

  const selection = useListSelection(sorted.map((c) => c.companyId));
  const bulkArchive = useBulkArchiveFiasLeadsCompanies(holderId);
  const bulkRestore = useBulkRestoreFiasLeadsCompanies(holderId);
  const bulkDelete = useBulkDeleteFiasLeadsCompanies(holderId);

  const handleBulk = async () => {
    const mutation = showArchived ? bulkRestore : bulkArchive;
    const verb = showArchived ? 'restored' : 'archived';
    try {
      const { results } = await mutation.mutateAsync(selection.selected);
      const failed = results.filter((r) => !r.ok).length;
      selection.clear();
      toast({
        title: `${results.length - failed} compan${results.length - failed === 1 ? 'y' : 'ies'} ${verb}`,
        ...(failed > 0
          ? { description: `${failed} could not be ${verb}.`, variant: 'destructive' as const }
          : {}),
      });
    } catch (err) {
      toast({
        title: `Could not ${showArchived ? 'restore' : 'archive'} — try again`,
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleBulkDelete = async () => {
    try {
      const { results } = await bulkDelete.mutateAsync(selection.selected);
      const summary = summarizeBulkDelete(results);
      selection.clear();
      setDeleteDialogOpen(false);
      toast({
        title: `${summary.deleted} compan${summary.deleted === 1 ? 'y' : 'ies'} permanently deleted`,
        ...(summary.failed > 0
          ? { description: summary.failureDescription, variant: 'destructive' as const }
          : {}),
      });
    } catch (err) {
      toast({
        title: 'Could not delete — try again',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex flex-col gap-4 p-6 max-w-6xl mx-auto">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Companies</h1>
          <p className="text-sm text-muted-foreground">
            Organisations you sell into. Shared with Fias 360 in this workspace.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowArchived((v) => !v);
              selection.clear();
            }}
            className="inline-flex items-center gap-2 rounded border border-border px-3 py-2 text-sm"
          >
            {showArchived ? 'Back to companies' : 'Show archived'}
          </button>
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-2 rounded bg-primary px-3 py-2 text-primary-foreground text-sm"
          >
            <Plus className="h-4 w-4" />
            New company
          </button>
        </div>
      </header>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          className="flex flex-wrap gap-2 rounded border border-border bg-card p-4"
        >
          <input
            type="text"
            placeholder="Company name *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 min-w-[200px] rounded border border-border bg-background px-3 py-2"
            maxLength={200}
            required
            autoFocus
          />
          <input
            type="text"
            placeholder="Domain (acme.com)"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="rounded border border-border bg-background px-3 py-2"
          />
          <input
            type="text"
            placeholder="Industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="rounded border border-border bg-background px-3 py-2"
          />
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!name.trim() || createCompany.isPending}
              className="rounded bg-primary px-4 py-2 text-primary-foreground disabled:opacity-50"
            >
              {createCompany.isPending ? 'Creating…' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(false)}
              className="rounded border border-border px-4 py-2"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <input
          type="search"
          placeholder="Search companies"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded border border-border bg-card pl-10 pr-3 py-2"
        />
      </div>

      <BulkArchiveBar
        mode={showArchived ? 'restore' : 'archive'}
        selectedCount={selection.selected.length}
        noun="company"
        nounPlural="companies"
        pending={bulkArchive.isPending || bulkRestore.isPending}
        onConfirm={() => void handleBulk()}
        onClear={selection.clear}
        onDelete={showArchived ? () => setDeleteDialogOpen(true) : undefined}
        deletePending={bulkDelete.isPending}
      />

      <BulkDeleteDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        count={selection.selected.length}
        noun="company"
        nounPlural="companies"
        onConfirm={handleBulkDelete}
        isDeleting={bulkDelete.isPending}
      />

      {showArchived && (
        <p className="text-sm text-muted-foreground">
          Archived companies — select any to restore them, or permanently delete them.
        </p>
      )}

      {companiesQuery.isLoading && <SkeletonRow />}
      {companiesQuery.isError && (
        <p className="text-destructive text-sm">
          Failed to load companies: {(companiesQuery.error as Error).message}
        </p>
      )}

      <div className="rounded border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40">
            <tr>
              <th className="w-10 px-4 py-2">
                <input
                  type="checkbox"
                  aria-label="Select all companies"
                  checked={selection.allSelected}
                  onChange={selection.toggleAll}
                />
              </th>
              <SortableTh
                label="Name"
                active={sort.sortKey === 'name'}
                direction={sort.direction}
                onClick={() => sort.toggle('name')}
                className="text-left px-4 py-2 font-medium"
              />
              <SortableTh
                label="Industry"
                active={sort.sortKey === 'industry'}
                direction={sort.direction}
                onClick={() => sort.toggle('industry')}
                className="text-left px-4 py-2 font-medium"
              />
              <SortableTh
                label="Domain"
                active={sort.sortKey === 'domain'}
                direction={sort.direction}
                onClick={() => sort.toggle('domain')}
                className="text-left px-4 py-2 font-medium"
              />
              <SortableTh
                label="Updated"
                active={sort.sortKey === 'updated'}
                direction={sort.direction}
                onClick={() => sort.toggle('updated')}
                className="text-left px-4 py-2 font-medium"
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted.map((c) => (
              <tr key={c.companyId} className="hover:bg-muted/30">
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    aria-label={`Select ${c.name}`}
                    checked={selection.isSelected(c.companyId)}
                    onChange={() => selection.toggle(c.companyId)}
                  />
                </td>
                <td className="px-4 py-2">
                  <Link
                    to={`${ARCHE_PATH}/companies/${c.companyId}`}
                    className="text-foreground hover:underline"
                  >
                    {c.name}
                  </Link>
                  <CrossArcheBadge present={companyPresence[c.companyId] === true} />
                </td>
                <td className="px-4 py-2 text-muted-foreground">{c.industry ?? '—'}</td>
                <td className="px-4 py-2 text-muted-foreground">{c.domain ?? '—'}</td>
                <td className="px-4 py-2 text-muted-foreground text-xs">
                  {new Date(c.updatedAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {(companiesQuery.data?.length ?? 0) >= 500 && (
          <p className="border-t border-border p-3 text-center text-xs text-muted-foreground">
            Showing the first 500 companies. Filter to narrow down, or archive some — the next 500
            load as the list empties.
          </p>
        )}
        {filtered.length === 0 && !companiesQuery.isLoading && (
          <p className="p-6 text-center text-muted-foreground text-sm">
            {search.trim()
              ? 'No companies match that search.'
              : 'No companies yet. Add your first one above.'}
          </p>
        )}
      </div>
    </div>
  );
}
